import { observer } from 'mobx-react';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useStore } from 'app/context';
import { IModalButton, Modal } from '../Modal';
import { Icon } from '../common/Icon';
import { notify } from '../common/notify';

type RecordingState = 'idle' | 'recording' | 'processing' | 'done';

interface IAudioTranscriptionModal {
	modalId: string;
	title?: string;
	/** Label of the primary button shown once a transcription is available. */
	doneLabel: string;
	/** Called with the (possibly edited) transcribed text when the user confirms. */
	onDone: (text: string) => void | Promise<void>;
	/** Called when the user dismisses the modal without confirming. */
	onCancel?: () => void;
}

function formatDuration(seconds: number): string {
	const m = Math.floor(seconds / 60);
	const s = seconds % 60;
	return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function getSupportedMimeType(): string {
	if (typeof MediaRecorder === 'undefined') return '';
	const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
	for (const type of types) {
		if (MediaRecorder.isTypeSupported(type)) return type;
	}
	return '';
}

/**
 * Reusable recording + transcription modal. Records audio from the microphone,
 * sends it to the transcription service and hands the resulting text back to the
 * caller via `onDone`. The caller decides what to do with the text (e.g. create a
 * document or insert it into an editor).
 */
export const AudioTranscriptionModal = observer((props: IAudioTranscriptionModal) => {
	const { uiStore, documentUiStore } = useStore();
	const modalId = props.modalId;

	const [state, setState] = useState<RecordingState>('idle');
	const [transcribedText, setTranscribedText] = useState('');
	const [duration, setDuration] = useState(0);
	const [error, setError] = useState<string | null>(null);

	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const chunksRef = useRef<Blob[]>([]);
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const streamRef = useRef<MediaStream | null>(null);

	const cleanup = useCallback(() => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
		}
		mediaRecorderRef.current = null;
		chunksRef.current = [];
	}, []);

	const resetState = useCallback(() => {
		cleanup();
		setState('idle');
		setTranscribedText('');
		setDuration(0);
		setError(null);
	}, [cleanup]);

	useEffect(() => {
		return cleanup;
	}, [cleanup]);

	const transcribeAudio = async (audioBlob: Blob, filename: string) => {
		setState('processing');
		try {
			const text = await documentUiStore.transcribeAudio(audioBlob, filename);
			setTranscribedText(text);
			setState('done');
		} catch (err: any) {
			notify('Fehler bei der Transkription', 'Bitte versuchen Sie es erneut.', 'error');
			setError('Transkription fehlgeschlagen.');
			setState('idle');
		}
	};

	const startRecording = async () => {
		setError(null);
		const mimeType = getSupportedMimeType();
		if (!mimeType) {
			setError('Ihr Browser unterstützt keine Audioaufnahme.');
			return;
		}

		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			streamRef.current = stream;

			const mediaRecorder = new MediaRecorder(stream, { mimeType });
			mediaRecorderRef.current = mediaRecorder;
			chunksRef.current = [];

			mediaRecorder.ondataavailable = (e) => {
				if (e.data.size > 0) {
					chunksRef.current.push(e.data);
				}
			};

			mediaRecorder.onstop = async () => {
				const mType = mimeType.split(';')[0];
				const audioBlob = new Blob(chunksRef.current, { type: mType });
				const ext = mType.includes('webm') ? 'webm' : mType.includes('ogg') ? 'ogg' : 'm4a';
				await transcribeAudio(audioBlob, `recording.${ext}`);
			};

			mediaRecorder.start(1000);
			setDuration(0);
			timerRef.current = setInterval(() => {
				setDuration((d) => d + 1);
			}, 1000);
			setState('recording');
		} catch (err: any) {
			if (err.name === 'NotAllowedError') {
				setError('Mikrofonzugriff wurde verweigert. Bitte erlauben Sie den Zugriff in den Browsereinstellungen.');
			} else {
				setError('Fehler beim Starten der Aufnahme: ' + err.message);
			}
		}
	};

	const stopRecording = () => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
		if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
			mediaRecorderRef.current.stop();
		}
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
		}
		setState('processing');
	};

	const onClose = () => {
		if (props.onCancel) {
			props.onCancel();
		}
		resetState();
		uiStore.hideModal(modalId);
	};

	const onDone = async () => {
		try {
			await props.onDone(transcribedText);
			uiStore.hideModal(modalId);
			resetState();
		} catch (err: any) {
			notify('Fehler', err.message, 'error');
		}
	};

	const getButtons = (): IModalButton[] => {
		switch (state) {
			case 'idle':
				return [
					{
						title: 'Aufnahme starten',
						className: 'button is-primary',
						onClick: startRecording,
					},
					{
						title: 'Abbrechen',
						className: 'button',
						onClick: onClose,
					},
				];
			case 'recording':
				return [
					{
						title: 'Aufnahme stoppen',
						className: 'button is-danger',
						onClick: stopRecording,
					},
				];
			case 'processing':
				return [
					{
						title: 'Abbrechen',
						className: 'button',
						onClick: onClose,
					},
				];
			case 'done':
				return [
					{
						title: props.doneLabel,
						className: 'button is-primary',
						onClick: onDone,
					},
					{
						title: 'Abbrechen',
						className: 'button',
						onClick: onClose,
					},
				];
			default:
				return [];
		}
	};

	return (
		<Modal modalId={modalId} title={props.title || 'Sprachaufnahme'} size="medium" buttons={getButtons()} onClose={onClose}>
			<div className="audio-recording-modal">
				{state === 'idle' && (
					<div className="recording-state">
						<div className="mic-icon">
							<Icon iconClass="microphone" faModifier="fas" />
						</div>
						<p className="instruction">Klicken Sie auf &quot;Aufnahme starten&quot;, um eine Sprachnotiz aufzunehmen.</p>
						{error && <p className="error-message">{error}</p>}
					</div>
				)}

				{state === 'recording' && (
					<div className="recording-state">
						<div className="recording-indicator">
							<span className="pulse-dot"></span>
							<span>Aufnahme läuft...</span>
						</div>
						<div className="timer">{formatDuration(duration)}</div>
					</div>
				)}

				{state === 'processing' && (
					<div className="recording-state">
						<div className="processing-icon">
							<Icon iconClass="spinner" faModifier="fas fa-pulse" />
						</div>
						<p className="instruction">Transkription wird durchgeführt...</p>
					</div>
				)}

				{state === 'done' && (
					<div className="transcription-result">
						<label>Transkribierter Text:</label>
						<textarea value={transcribedText} onChange={(e) => setTranscribedText(e.target.value)} rows={10} />
					</div>
				)}
			</div>
		</Modal>
	);
});
